import { useSearchParams } from 'react-router';

import type { SensTri, StatutIntervention, TriInterventions } from '../api/interventions';
import { useCriteresUrlGenerique } from './useCriteresUrlGenerique';

const STATUTS: readonly StatutIntervention[] = ['planifiee', 'en-cours', 'terminee', 'annulee'];
const TRIS: readonly TriInterventions[] = ['date', 'client'];
const PERIODES: readonly string[] = ['semaine', 'mois', 'annee', 'tout'];

export interface CriteresUrl {
  readonly recherche: string;
  readonly statut: StatutIntervention | '';
  /** « 1 » : seulement les interventions en retard (critère de l'alerte d'accueil). */
  readonly retard: boolean;
  /** Fenêtre glissante : `semaine` · `mois` · `annee` · `tout`. */
  readonly periode: string;
  /** Jour exact posé par un clic dans le calendrier (`AAAA-MM-JJ`). */
  readonly date: string;
  readonly tri: TriInterventions;
  readonly sens: SensTri;
  readonly page: number;
}

/**
 * Critères de l'écran « Mes interventions », portés par l'URL.
 *
 * Simple habillage du hook générique : la mécanique (URL source unique, page
 * retirée à 1, frappe en `replace`) n'est écrite qu'une fois pour tous les écrans.
 * Une valeur hors liste (`tri` inconnu, `periode` inventée) retombe sur le défaut
 * ICI, et le serveur les refuse de son côté : deux garde-fous, pas deux vérités.
 */
export function useCriteresUrl(): CriteresUrl & {
  setRecherche: (valeur: string) => void;
  setStatut: (valeur: StatutIntervention | '') => void;
  setRetard: (valeur: boolean) => void;
  setPeriode: (valeur: string) => void;
  setDate: (valeur: string) => void;
  setTri: (valeur: TriInterventions, sens: SensTri) => void;
  setPage: (valeur: number) => void;
  toutEffacer: () => void;
} {
  const { recherche, filtre, page, setRecherche, setFiltre, setPage, toutEffacer } =
    useCriteresUrlGenerique<StatutIntervention>('statut', STATUTS);

  // Les autres critères vivent dans la MÊME URL : un lien du tableau de bord ou
  // du calendrier doit survivre à un rechargement de page.
  const [parametres, setParametres] = useSearchParams();

  const retard = parametres.get('retard') === '1';
  const periodeBrute = parametres.get('periode') ?? 'tout';
  const periode = PERIODES.includes(periodeBrute) ? periodeBrute : 'tout';
  const date = parametres.get('date') ?? '';
  const triBrut = parametres.get('tri') ?? 'date';
  const tri = (TRIS.find((valeur) => valeur === triBrut) ?? 'date') as TriInterventions;
  // Le sens DÉCROISSANT est le défaut du serveur pour cette liste.
  const sens: SensTri = parametres.get('sens') === 'asc' ? 'asc' : 'desc';

  const poser = (changements: Record<string, string>) => {
    const suivants = new URLSearchParams(parametres);
    for (const [cle, valeur] of Object.entries(changements)) {
      if (valeur === '') suivants.delete(cle);
      else suivants.set(cle, valeur);
    }
    // Tout changement de critère ramène à la première page.
    suivants.delete('page');
    setParametres(suivants, { replace: false });
  };

  return {
    recherche,
    statut: filtre,
    retard,
    periode,
    date,
    tri,
    sens,
    page,
    setRecherche,
    setStatut: setFiltre,
    setRetard: (valeur: boolean) => poser({ retard: valeur ? '1' : '' }),
    setPeriode: (valeur: string) => poser({ periode: valeur === 'tout' ? '' : valeur }),
    setDate: (valeur: string) => poser({ date: valeur }),
    setTri: (valeur: TriInterventions, direction: SensTri) =>
      poser({ tri: valeur === 'date' ? '' : valeur, sens: direction === 'desc' ? '' : 'asc' }),
    setPage,
    // Le générique efface TOUTE la requête, donc les autres critères avec.
    toutEffacer,
  };
}
